/**
 * The pair × timeframe data-health matrix. Task 32, and the S11 panel it is named for.
 *
 * `UI-Design-Spec.md#s11`:
 *
 * > Market-data health is a matrix, pair × timeframe, one cell per series, coloured by
 * > staleness only: `--bg-inset` fresh, `--attn` ageing, `--loss` stale, with a hatched cell
 * > for a known gap. A missing bar is the failure this matrix exists to surface, and
 * > hatching distinguishes *gap* from *late* without a second colour.
 *
 * Every one of those rules belongs to `StatusMatrixCell`, which is why this file draws no
 * colours of its own. What it owns is the **shape of the grid**, and there is one decision
 * in that which the endpoint cannot make.
 *
 * ## The empty cell is the point
 *
 * `/data-health` returns one row per series that *has bars*. Rendering only what it returns
 * gives a ragged grid where a symbol with 1m and no 1h simply has fewer cells — and a
 * timeframe that was never aggregated looks identical to a timeframe nobody wanted. That is
 * the failure this screen exists to surface, hidden by the screen.
 *
 * So the grid is **every symbol seen × all six timeframes**, and a combination the endpoint
 * did not return renders as an explicit *no series* cell rather than as nothing. On the real
 * dev data that immediately shows something true: eleven symbols hold a single `1d` bar and
 * nothing else.
 *
 * **What it still cannot show** is a symbol that *should* be ingested and is entirely absent,
 * because nothing anywhere records which symbols are wanted — universes are not built. A
 * symbol missing from every row is a finding this matrix is structurally unable to make, and
 * saying so in the footnote is the honest substitute for a row that cannot exist.
 */

import { Timeframe, type SeriesHealthResponse } from '../api/generated';
import { StatusMatrixCell } from '../design/components/data/StatusMatrixCell.jsx';
import { MODE_LABELS, type TimestampMode, useTimestampMode } from '../ui/timestamps';

/** The columns, in the order the platform aggregates them — not alphabetical. */
export const TIMEFRAMES: Timeframe[] = [
  Timeframe._1M,
  Timeframe._5M,
  Timeframe._15M,
  Timeframe._1H,
  Timeframe._4H,
  Timeframe._1D,
];

export interface DataHealthMatrixProps {
  series: SeriesHealthResponse[];
  /** Clicking a cell opens that series on the chart. */
  onOpen?: (instrumentId: number, timeframe: Timeframe) => void;
}

interface Row {
  symbol: string;
  instrumentId: number;
  cells: Map<string, SeriesHealthResponse>;
}

export function rowsOf(series: SeriesHealthResponse[]): Row[] {
  const rows = new Map<string, Row>();
  for (const entry of series) {
    let row = rows.get(entry.symbol);
    if (!row) {
      row = { symbol: entry.symbol, instrumentId: entry.instrument_id, cells: new Map() };
      rows.set(entry.symbol, row);
    }
    row.cells.set(entry.timeframe, entry);
  }
  return [...rows.values()].sort((a, b) => a.symbol.localeCompare(b.symbol));
}


/**
 * `2026-07-31 09:06:00` in the requested mode — the tooltip's own shape, which wants the calendar
 * as well as the clock because a bar's date is what "stale" is measured against.
 */
function stampOf(iso: string, mode: TimestampMode): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return mode === 'utc'
    ? `${when.getUTCFullYear()}-${pad(when.getUTCMonth() + 1)}-${pad(when.getUTCDate())} ` +
        `${pad(when.getUTCHours())}:${pad(when.getUTCMinutes())}:${pad(when.getUTCSeconds())}`
    : `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())} ` +
        `${pad(when.getHours())}:${pad(when.getMinutes())}:${pad(when.getSeconds())}`;
}

/**
 * The native tooltip, in the shape the component's own prompt gives as the example.
 *
 * ⚠ **`mode` is threaded in rather than read from the store, because this is a pure function** —
 * `DataHealthMatrix.test.ts` calls it directly, and a hook here would make it untestable without a
 * renderer. The component reads the store once and passes it down.
 *
 * It used to append a hardcoded `UTC`, which became a lie the moment `UI-Design-Spec.md` line 120's
 * toggle existed: the matrix would have said `UTC` while the header said `Local`, which is the
 * mixed-timezone misreading the spec gives as the reason for having a mode at all.
 */
export function titleFor(
  symbol: string,
  timeframe: string,
  cell?: SeriesHealthResponse,
  mode: TimestampMode = 'utc',
): string {
  if (!cell) return `${symbol} ${timeframe} · no series — never ingested or never aggregated`;
  const parts = [
    `${symbol} ${timeframe}`,
    `last bar ${stampOf(cell.last_bar_ts, mode)} ${MODE_LABELS[mode]}`,
    `${cell.bars.toLocaleString('en-GB')} bars`,
    cell.freshness,
  ];
  if (cell.open_gaps > 0) {
    const gaps = cell.open_gaps === 1 ? '1 unfilled gap' : `${cell.open_gaps} unfilled gaps`;
    parts.push(`${cell.missing_bars.toLocaleString('en-GB')} bars missing across ${gaps}`);
  }
  return parts.join(' · ');
}

export function DataHealthMatrix({ series, onOpen }: DataHealthMatrixProps): React.JSX.Element {
  const rows = rowsOf(series);
  // Once for the grid, then passed into the pure `titleFor` for each cell.
  const mode = useTimestampMode();

  return (
    <table className="jmt-matrix">
      <thead>
        <tr>
          <th />
          {TIMEFRAMES.map((timeframe) => (
            <th key={timeframe}>{timeframe}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.symbol}>
            <th scope="row">{row.symbol}</th>
            {TIMEFRAMES.map((timeframe) => {
              const cell = row.cells.get(timeframe);
              const title = titleFor(row.symbol, timeframe, cell, mode);
              return (
                <td key={timeframe}>
                  {cell ? (
                    <button
                      type="button"
                      className="jmt-matrix__cell"
                      onClick={() => onOpen?.(row.instrumentId, timeframe)}
                      title={title}
                      aria-label={title}
                    >
                      <StatusMatrixCell
                        state={cell.freshness}
                        gap={cell.open_gaps > 0}
                        title={title}
                      />
                    </button>
                  ) : (
                    // Absence has its own mark, and it is not a fourth colour: an outlined
                    // empty square, at the same size, so the grid stays a grid and the hole
                    // is visible as a hole.
                    //
                    // "At the same size" was not true. This was 20px with a `--radius-sm`
                    // that the system does not define, sitting next to `StatusMatrixCell`
                    // siblings that default to 26px and are square — so the holes were both
                    // undersized and differently shaped, which read as a third kind of cell
                    // rather than as an absence. `.jmt-matrix__empty` matches the sibling.
                    <span className="jmt-matrix__empty" title={title} aria-label={title} />
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
